import app from "@/main";
import {devreKesiciAcikMi} from "@/plugins/uyap-plugin/BaglantiKurtarma";
import {PlayerDurum} from "@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes";
import {uyapEngelKilidiAktifMi} from "@/plugins/uyap-plugin/UyapEngeli";
import {
    OnKontrolDurumu,
    onKontrolMesajiOlustur
} from "@/plugins/uyap-plugin/senkronOnKontrolMesaji";
import {isletimSistemiBildirimi, uyariSesi} from "@/plugins/kritikUyari";
import senkronPlanService from "@/services/senkronPlanService";
import {SENKRON_KAPI_ATESLEME_ARALIGI_MS, senkronAcikMiTazeleyerek} from "@/utils/senkronKapisi";

/**
 * Gece senkronu ön-kontrolü — uygulama tarafı (Faz 3, DEV-2967).
 *
 * Zamanlayıcı `uyap-plugin/SenkronOnKontrolZamanlayici`de; burada NE kontrol
 * edileceği ve bildirimin NASIL gösterileceği var. Ayrımın sebebi
 * `BaglantiKurtarma`nınkiyle aynı: plugin'i iki uygulama kullanıyor ve plugin
 * store/DOM'a bağlanmıyor.
 *
 * GÖREV YÖNETİCİSİ'NE DOKUNULMAZ — buradaki tek okuma `getPlayerDurum`
 * getter'ıdır; hiçbir görev üretilmez, kuyruk değiştirilmez.
 */

/**
 * UYAP oturumunun gerçekten canlı olup olmadığını yoklar.
 *
 * `probe()` "sor, kurtarmaya çalışma" demektir: kurtarma yolu bot istekleri için
 * tasarlandı ve yoklamayı 48 sn bekletiyor. Ayrıca bağlantı bayrağı ZATEN false
 * ise hiç istek atılmaz — cevabı bildiğimiz bir soruyu UYAP'a sormanın anlamı
 * yok (istek trafiği ban riskiyle ilgili).
 */
async function uyapOturumuCanliMi(getters: any): Promise<boolean> {
    if (!getters || getters.isUyapConnected !== true) return false;
    // UYAP engeli sürüyor (DEV-3572): yoklama kapıdan muaf bir uçtur ama engel sürerken UYAP'a ek istek ATILMAZ.
    if (uyapEngelKilidiAktifMi()) return false;

    try {
        await app.$uyap.KullaniciBilgileri().probe().run({});
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Kontrol listesini toplar. Hiçbir adım diğerini düşürmez.
 *
 * `store` çağırandan gelir; `app.$store` yalnız yedektir (bkz. `App.vue`
 * `senkronZamanlayicilariniKur` yorumu).
 */
export async function onKontrolDurumunuTopla(store?: any): Promise<OnKontrolDurumu> {
    const getters = (store ?? (app ? app.$store : null))?.getters ?? null;

    let planlarOkunabildi = true;
    let aktifPlanSayisi = 0;

    try {
        const planlar = await senkronPlanService.listele();
        aktifPlanSayisi = planlar.filter((plan) => plan.aktif).length;
    } catch (e) {
        console.error("Ön-kontrol: senkron planları okunamadı:", e);
        planlarOkunabildi = false;
    }

    // Plan listesi okunabildi ve aktif plan yoksa mesaj üretilmez (`onKontrolMesajiOlustur`
    // null döner): oturum durumunu kimse kullanmaz. UYAP'ı bu durumda yoklamak, planı
    // olmayan her kullanıcıda her gece boş yere istek (ban riski) göndermek olurdu.
    const planYok = planlarOkunabildi && aktifPlanSayisi < 1;

    return {
        planlarOkunabildi,
        aktifPlanSayisi,
        uyapOturumuCanli: planYok ? false : await uyapOturumuCanliMi(getters),
        imzaBagli: getters ? getters.isImzaConnected === true : false,
        botOtoda: getters ? getters.getPlayerDurum === PlayerDurum.oto : false,
        devreKesiciAcik: devreKesiciAcikMi()
    };
}

/**
 * Ön-kontrolü koşar ve gerekiyorsa bildirim gösterir.
 *
 * Aktif plan yokken HİÇ bildirim çıkmaz: senkronu kurmamış kullanıcıyı her gece
 * 22:45'te dürtmenin bir anlamı olmazdı.
 *
 * OTOMATİK SORGU KAPISI (DEV-2603, karar 20.10.2026): kapı kapalıyken HİÇBİR şey
 * yapılmaz — plan listesi okunmaz, `probe()` ile UYAP yoklanmaz, bildirim/ses yok.
 * Kapıyı kapatıp bunu açık bırakmak her kullanıcıya her gece "Senkron planları
 * sunucudan okunamadı" diye SAHTE bir alarm üretirdi. Kapı ateşleme ANINDA okunur
 * (kurulumda değil): kullanıcı bilgisi kurulumdan sonra yüklenmiş ya da kapı
 * kapanmış olabilir. Önbellekteki değer kapalıysa istek atılmadan çıkılır; açıksa
 * sunucudan (sessiz, 10 dk'dan taze değilse) teyit edilir: günlerce açık kalmış bir
 * sekme, sunucu sonradan yeniden kapatılsa da UYAP'ı yoklamasın. Store bilinmiyorsa
 * kapı da bilinmiyor demektir: KAPALI.
 */
export async function senkronOnKontroluCalistir(store?: any): Promise<void> {
    if (!(await senkronAcikMiTazeleyerek(store ?? (app ? app.$store : null), SENKRON_KAPI_ATESLEME_ARALIGI_MS))) return;

    // UYAP engeli (DEV-3572, SOZLESME §2.6): kilit sürerken ön-kontrol HİÇBİR şey yapmaz: ne istek, ne "UYAP oturumu
    // düşmüş; e-imzayla yeniden bağlanın" alarmı (engel sırasında yanlış yönlendirme), ne de "Gece senkronu hazır"
    // yalanı. Engel şeridi/penceresi zaten görünür.
    if (uyapEngelKilidiAktifMi()) return;

    try {
        const durum = await onKontrolDurumunuTopla(store);
        // Yoklamanın KENDİSİ engele çarpmış olabilir: bildirim yok.
        if (uyapEngelKilidiAktifMi()) return;
        const mesaj = onKontrolMesajiOlustur(durum);
        if (!mesaj) return;

        isletimSistemiBildirimi(mesaj.baslik, mesaj.metin);
        // Ses yalnız eksik varken: her gece çalan bir "hazır" sesi kanıksanır ve
        // sinyal değerini yitirir (kritikUyari.ts'in kendi gerekçesi).
        if (mesaj.kritikMi) uyariSesi();
    } catch (e) {
        console.error("Senkron ön-kontrolü koşturulamadı:", e);
    }
}
